import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as L from "lucide-react";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, FileText, Sparkles, RotateCcw, Info } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AIAuditBadge from "@/components/AIAuditBadge";

// Maps wizard project_type → ApplicationWizard case_type key
const APPLICATION_TYPE_MAP = {
  altan: "Annat",
  tillbyggnad: "Tillbyggnad",
  friggebod: "Komplementbyggnad",
  attefallshus: "Komplementbyggnad",
  skarmtak: "Annat",
  mur_plank: "Annat",
  pool: "Annat",
  fasadandring: "Fasadändring",
  uterum: "Tillbyggnad",
  solceller: "Annat",
  rivning: "Rivning",
  eldstad: "Annat",
  andrad_anvandning: "Ändrad användning",
  takkupa: "Tillbyggnad",
  balkong: "Tillbyggnad",
  carport: "Komplementbyggnad",
  vaermepump: "Annat",
  byte_tak: "Annat",
};

const VERDICT_META = {
  lovbefriad: { color: "bg-emerald-50 border-emerald-300 text-emerald-900",
                badge: "bg-emerald-700", label: "Lovbefriad", icon: CheckCircle2,
                text: "Du kan börja bygga direkt – ingen kontakt med kommunen krävs." },
  anmalan: { color: "bg-amber-50 border-amber-300 text-amber-900",
             badge: "bg-amber-700", label: "Anmälan krävs", icon: FileText,
             text: "Du måste anmäla åtgärden och invänta startbesked innan du börjar." },
  bygglov: { color: "bg-blue-50 border-blue-300 text-blue-900",
             badge: "bg-blue-700", label: "Bygglov krävs", icon: FileText,
             text: "Du måste ansöka om bygglov och invänta beslut innan du börjar." },
  rivningslov: { color: "bg-orange-50 border-orange-300 text-orange-900",
             badge: "bg-orange-700", label: "Rivningslov krävs", icon: FileText,
             text: "Inom detaljplan krävs rivningslov från kommunen innan rivning." },
  rivningsanmalan: { color: "bg-amber-50 border-amber-300 text-amber-900",
             badge: "bg-amber-700", label: "Rivningsanmälan krävs", icon: FileText,
             text: "Utanför detaljplan räcker rivningsanmälan + startbesked." },
  marklov: { color: "bg-violet-50 border-violet-300 text-violet-900",
             badge: "bg-violet-700", label: "Marklov krävs", icon: FileText,
             text: "Schaktning/fyllning kräver marklov från kommunen." },
  dispens_required: { color: "bg-red-50 border-red-300 text-red-900",
             badge: "bg-red-700", label: "Dispens krävs", icon: AlertTriangle,
             text: "Strandskydds- eller annan dispens måste sökas innan annan prövning." },
  uncertain: { color: "bg-zinc-50 border-zinc-300 text-zinc-900",
             badge: "bg-zinc-700", label: "Osäkert", icon: Info,
             text: "Reglerna är inte entydiga för ditt fall – kontakta kommunen." },
};

function Icon({ name, className }) {
  const Cmp = L[name] || L.Box;
  return <Cmp className={className} />;
}

export default function PermitWizard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/permit/project-types").then((r) => setTypes(r.data));
  }, []);

  const resetAll = () => {
    setSelectedType(null);
    setAnswers({});
    setResult(null);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/permit/wizard", {
        project_type: selectedType.id,
        answers,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally { setSubmitting(false); }
  };

  const startApplication = () => {
    if (!result || !selectedType) return;
    const lines = [];
    lines.push(`Projekttyp: ${result.project_label}`);
    lines.push(`Bedömning: ${result.verdict_label}`);
    lines.push("");
    lines.push("Mina svar:");
    selectedType.questions.forEach((q) => {
      const v = answers[q.key];
      if (v === undefined || v === "") return;
      const display = v === true ? "Ja" : v === false ? "Nej" : `${v}${q.unit ? " " + q.unit : ""}`;
      lines.push(`  • ${q.label}: ${display}`);
    });
    if (result.required_documents?.length) {
      lines.push("");
      lines.push("Handlingar enligt regelmotorn:");
      result.required_documents.forEach((d) => lines.push(`  ☐ ${d}`));
    }
    const prefill = {
      case_type: APPLICATION_TYPE_MAP[selectedType.id] || "Annat",
      title: result.project_label,
      description: lines.join("\n"),
      permit_checklist: result.required_documents || [],
      permit_meta: {
        project_type: selectedType.id,
        verdict: result.verdict,
        verdict_label: result.verdict_label,
      },
      source: "permit_wizard",
      created_at: new Date().toISOString(),
    };
    try { sessionStorage.setItem("permit_prefill", JSON.stringify(prefill)); } catch (e) { /* ignore quota */ }
    if (user) {
      nav("/ansok");
    } else {
      nav("/login?next=/ansok");
    }
  };

  // STEP 1 – Pick project type
  if (!selectedType) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-700 mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Bygglovs-guide enligt PBL & BBR
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Vilken typ av projekt?
          </h1>
          <p className="mt-3 text-base text-zinc-600 max-w-xl mx-auto">
            Välj ditt projekt så ställer vi rätt frågor och ger dig exakt vad som krävs – baserat på PBL, BBR och Boverkets praxis.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="permit-type-grid">
          {types.map((t) => (
            <button key={t.id} onClick={() => setSelectedType(t)}
              data-testid={`type-${t.id}`}
              className="text-left bg-white border border-zinc-200 hover:border-blue-700 hover:shadow-md rounded-sm p-5 transition group">
              <div className="w-10 h-10 bg-blue-50 text-blue-700 rounded-sm flex items-center justify-center mb-3 group-hover:bg-blue-700 group-hover:text-white transition">
                <Icon name={t.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>{t.label}</h3>
              <p className="mt-1.5 text-xs text-zinc-600 leading-relaxed line-clamp-3">{t.summary}</p>
              <div className="mt-3 text-xs text-blue-700 inline-flex items-center gap-1">
                Starta guiden <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // STEP 3 – Result
  if (result) {
    const meta = VERDICT_META[result.verdict] || VERDICT_META.uncertain;
    const VIcon = meta.icon;
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
        <button onClick={resetAll} data-testid="restart-wizard"
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-6">
          <RotateCcw className="w-3.5 h-3.5" /> Starta om
        </button>

        <div className={`border-2 rounded-sm p-7 ${meta.color}`} data-testid="permit-result">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 ${meta.badge} text-white rounded-sm flex items-center justify-center flex-shrink-0`}>
              <VIcon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-70">{result.project_label}</div>
              <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{meta.label}</h1>
              <p className="mt-1.5 text-sm leading-relaxed">{meta.text}</p>
            </div>
          </div>
        </div>

        <Section title="AI-resonemang" testid="result-reasoning">
          <ul className="space-y-1.5 text-sm">
            {result.reasoning.map((r, i) => (
              <li key={i} className="text-zinc-700">{r}</li>
            ))}
          </ul>
        </Section>

        {result.law_refs?.length > 0 && (
          <Section title="Lagrum">
            <div className="flex flex-wrap gap-2">
              {result.law_refs.map((r, i) => (
                <span key={i} className="px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">{r}</span>
              ))}
            </div>
          </Section>
        )}

        {result.warnings?.length > 0 && (
          <Section title="Att tänka på" testid="result-warnings">
            <ul className="space-y-2 text-sm">
              {result.warnings.map((w, i) => (
                <li key={i} className="flex gap-2 text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {result.required_documents?.length > 0 && (
          <Section title={`Handlingar som krävs (${result.required_documents.length})`} testid="result-documents">
            <ul className="space-y-1.5 text-sm">
              {result.required_documents.map((d, i) => (
                <li key={i} className="flex gap-2 text-zinc-800">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-emerald-600" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {result.verdict !== "lovbefriad" && (
            <button onClick={startApplication} data-testid="start-application"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm">
              {user ? "Skicka in ansökan nu" : "Logga in & ansök"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button onClick={resetAll}
            className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:bg-zinc-50 text-sm rounded-sm">
            Kontrollera annat projekt
          </button>
          {result.audit_id && <AIAuditBadge auditId={result.audit_id} kind="permit_check" />}
        </div>
      </div>
    );
  }

  // STEP 2 – Question form
  const fillCount = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== "").length;
  const allRequiredFilled = selectedType.questions
    .filter((q) => q.required)
    .every((q) => answers[q.key] !== undefined && answers[q.key] !== "");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <button onClick={() => setSelectedType(null)} data-testid="back-to-types"
        className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-5">
        <ArrowLeft className="w-3.5 h-3.5" /> Välj annan projekttyp
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 bg-blue-700 text-white rounded-sm flex items-center justify-center flex-shrink-0">
          <Icon name={selectedType.icon} className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-700">Bygglovs-guide · Steg 2</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{selectedType.label}</h1>
          <p className="mt-1 text-sm text-zinc-600">{selectedType.summary}</p>
        </div>
      </div>

      {/* Rules summary */}
      <details className="mt-5 mb-7 group" data-testid="rules-summary">
        <summary className="cursor-pointer text-xs uppercase tracking-wider font-semibold text-zinc-500 hover:text-zinc-800">
          Visa relevanta regler från PBL/BBR ▾
        </summary>
        <ul className="mt-3 space-y-1.5 text-sm text-zinc-700 bg-blue-50 border border-blue-200 rounded-sm p-4">
          {selectedType.rules_summary.map((r, i) => (
            <li key={i} className="flex gap-2"><Info className="w-3.5 h-3.5 mt-0.5 text-blue-600 flex-shrink-0" /> {r}</li>
          ))}
        </ul>
      </details>

      {/* Questions */}
      <div className="space-y-4">
        {selectedType.questions.map((q) => (
          <Question key={q.key} q={q}
            value={answers[q.key]}
            onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
        ))}
      </div>

      <div className="mt-7 flex items-center justify-between gap-3">
        <span className="text-xs text-zinc-500">{fillCount} av {selectedType.questions.length} besvarade</span>
        <button onClick={submit} disabled={!allRequiredFilled || submitting}
          data-testid="evaluate-permit"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Få mitt svar <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Question({ q, value, onChange }) {
  const id = `q-${q.key}`;
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={`question-${q.key}`}>
      <label htmlFor={id} className="block text-sm font-medium text-zinc-900">
        {q.label} {q.required && <span className="text-red-600">*</span>}
      </label>
      {q.type === "number" && (
        <div className="mt-2 flex items-center gap-2">
          <input id={id} type="number" step="0.01" value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            data-testid={`input-${q.key}`}
            className="w-32 px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-700 outline-none" />
          {q.unit && <span className="text-sm text-zinc-500">{q.unit}</span>}
        </div>
      )}
      {q.type === "boolean" && (
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={() => onChange(true)}
            data-testid={`input-${q.key}-yes`}
            className={`px-4 py-1.5 text-sm font-medium rounded-sm border ${value === true ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>Ja</button>
          <button type="button" onClick={() => onChange(false)}
            data-testid={`input-${q.key}-no`}
            className={`px-4 py-1.5 text-sm font-medium rounded-sm border ${value === false ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>Nej</button>
        </div>
      )}
      {q.type === "select" && (
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          data-testid={`input-${q.key}`}
          className="mt-2 w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm bg-white">
          <option value="">Välj...</option>
          {q.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      )}
    </div>
  );
}
